import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { HubConnectionState } from "@microsoft/signalr";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Rnd } from "react-rnd";
import { useNavigate } from "react-router-dom";
import { CONTROL_ICONS } from "../../assets/content/controlIcons";
import droneSound from "../../assets/images/droneSound.svg";
import droneSoundMute from "../../assets/images/droneSoundMute.svg";
import { NotificationType } from "../../enum";
import "./DroneControlBottomPanel.css";
import { useSignalR } from "../../hooks/SignalR/SignalRContext";
import missionService from "../../services/missionService";
import sessionManager from "../../services/sessionManager";
import videoService from "../../services/videoService";
import { setNotificationMessage } from "../../slices/notificationMessageSlice";
import { setNotificationStatus } from "../../slices/notificationStatusSlice";
import { setCamerasRatio, setShowParkingGrid, } from "../../slices/setActiveCameraSlice";
import { useAppDispatch, useAppSelector } from "../../storeHooks";
import { DirectionControllerSpeed } from "../DirectionController/DirectionControllerSpeed";
import ArmedStatus from "../DroneControlPanel/components/ArmedStatus/ArmedStatus";
import Autopilot from "../DroneControlPanel/components/Autopilot/Autopilot";
import VideoStatus from "../DroneControlPanel/components/VideoStatus/VideoStatus";
import { Map } from "../Map/Map";
const FRONT_CAMERA_INDEX = 0;
const REAR_CAMERA_INDEX = 1;
export const DroneControlBottomPanel = ({ droneId, isArmed, onArmedChange, isViewOnly, isFullSpeed, setIsStickSend, onCruiseControlClick, isCruiseControlShown, onExit, cameras, setMuteMic, setMuteAudio, setMicVolume, }) => {
    const { activeCameraId } = useAppSelector((store) => ({
        activeCameraId: store.activeCamera.value.activeCamera,
    }));
    const { t } = useTranslation();
    const [showMap, setShowMap] = useState(false);
    const [showAutopilot, setShowAutopilot] = useState(false);
    const [currPos, setCurrPos] = useState({
        lat: 50.016709,
        lng: 36.296305,
    });
    const [currAzimuth, setCurrAzimuth] = useState(0);
    const [isMainVideo, setIsMainVideo] = useState(true);
    const [isRollbackEnabled, setIsRollbackEnabled] = useState(false);
    const [gear, setGear] = useState(1);
    const rollbackState = useRef(false);
    const [routePoints, setRoutePoints] = useState([]);
    const [destinationPoints, setDestinationPoints] = useState([]);
    const [missionId, setMissionId] = useState(undefined);
    let storedMissionId = useRef(undefined);
    const [isCameraModeNight, setIsCameraModeNight] = useState(false);
    const [isCameraLightOn, setIsCameraLightOn] = useState(false);
    const [isFrontCameraLightOn, setIsFrontCameraLightOn] = useState(false);
    const [isRearCameraLightOn, setIsRearCameraLightOn] = useState(false);
    const [hasSpeaker, setHasSpeaker] = useState(false);
    const [hasMic, setHasMic] = useState(false);
    const [activeCameraHasSpeaker, setActiveCameraSpeaker] = useState(false);
    const [isAudio, setIsAudio] = useState(true);
    const [isMic, setMic] = useState(true);
    const [sliderVolume, setSliderVolume] = useState(0.3);
    const connection = useSignalR();
    const [capabilities, setCapabilities] = useState([]);
    const [mapSize, setMapSize] = useState({ w: 320, h: 320 });
    const [isAutopilot, setIsAutopilot] = useState(false);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const versions = useAppSelector((store) => store.versions.value);
    const showParkingGrid = useAppSelector((store) => store.activeCamera.value.showParkingGrid);
    const [selectedLayer, setSelectedLayer] = useState(0);
    const hasParkingGridFoV = cameras.some((camera) => !!camera.hFoV && !!camera.vFoV);
    useEffect(() => {
        const cameraQuality = isMainVideo
            ? "mainAspectRatio"
            : "alternativeAspectRatio";
        dispatch(setCamerasRatio(cameraQuality));
    }, [isMainVideo]);
    useEffect(() => {
        const camerasWithSpeakerIndexes = cameras
            .map((item, index) => (item.hasSpeaker ? index : -1))
            .filter((index) => index !== -1);
        setHasSpeaker(camerasWithSpeakerIndexes.length > 0);
        const camerasWithMicIndexes = cameras
            .map((item, index) => (item.hasMicrophone ? index : -1))
            .filter((index) => index !== -1);
        setHasMic(camerasWithMicIndexes.length > 0);
        const activeCameraHasSpeaker = camerasWithSpeakerIndexes.includes(activeCameraId);
        if (activeCameraHasSpeaker) {
            setActiveCameraSpeaker(true);
        }
        else {
            setActiveCameraSpeaker(false);
        }
    }, [activeCameraId, cameras]);
    useEffect(() => {
        if (connection) {
            connection.onreconnected(async () => {
                //setIsOperatorOnline(true);
                await connection.invoke("UpdateRollbackState", rollbackState.current);
            });
            connection.on("DroneConnected", onDroneConnected);
            connection.on("Status", onStatus);
            connection.on("SendCameraLightStatus", onChangeCameraLight);
            connection.on("SendCameraLightStatusV2", onChangeCameraLightV2);
            connection.on("ConfirmSpeed", onConfirmSpeed);
            connection.invoke("UpdateRollbackState", rollbackState.current);
            connection.on("AddRoutePoint", onAddRoutePoint);
            connection.on("SendCameraModeStatus", onChangeCameraMode);
            connection.on("SendSoftwareVersion", onSendSoftwareVersion);
            return () => {
                connection.off("DroneConnected", onDroneConnected);
                connection.off("Status", onStatus);
                connection.off("SendCameraLightStatus", onChangeCameraLight);
                connection.off("SendCameraLightStatusV2", onChangeCameraLightV2);
                connection.off("ConfirmSpeed", onConfirmSpeed);
                connection.off("AddRoutePoint", onAddRoutePoint);
                connection.off("SendCameraModeStatus", onChangeCameraMode);
                connection.off("SendSoftwareVersion", onSendSoftwareVersion);
            };
        }
    }, [connection]);
    const onDroneConnected = () => {
        if (connection && connection.state == HubConnectionState.Connected) {
            connection.invoke("UpdateRollbackState", rollbackState.current);
            connection.invoke("Speed", gear * 25);
        }
        if (!missionId) {
            if (sessionManager.isPageRefreshed()) {
                missionService
                    .continueMission(droneId)
                    .then((data) => {
                    console.log("Continue mission:", data);
                    setMissionId(data.missionId);
                    setRoutePoints(data.routePoints.map((p) => {
                        return { lat: p.latitude, lng: p.longitude, alt: p.altitude };
                    }));
                    setDestinationPoints(data.destinationPoints.map((p) => {
                        return { lat: p.latitude, lng: p.longitude };
                    }));
                })
                    .catch();
            }
            else {
                missionService
                    .startMission(droneId)
                    .then((data) => {
                    console.log("Mission start:", data);
                    setMissionId(data.missionId);
                    setRoutePoints(data.routePoints.map((p) => {
                        return { lat: p.latitude, lng: p.longitude, alt: p.altitude };
                    }));
                    setDestinationPoints(data.destinationPoints.map((p) => {
                        return { lat: p.latitude, lng: p.longitude };
                    }));
                })
                    .catch();
            }
        }
    };
    const onStatus = (status) => {
        setIsMainVideo(status.isMainVideoStream);
        setGear(status.gear);
        setIsCameraModeNight(status.isCameraModeNight);
    };
    const onConfirmSpeed = (value) => {
        setGear(value / 25);
    };
    const onAddRoutePoint = (model) => {
        setCurrPos({ lat: model.latitude, lng: model.longitude });
        setCurrAzimuth(model.azimuth);
        if (model.isRoutePoint) {
            setRoutePoints((prev) => [
                ...prev,
                [model.latitude, model.longitude, model.altitude],
            ]);
        }
    };
    const onChangeCameraMode = (isNightMode) => {
        setIsCameraModeNight(isNightMode);
    };
    const onChangeCameraLight = (isLightMode) => {
        setIsCameraLightOn(isLightMode);
    };
    const onChangeCameraLightV2 = (cameraIndex, isLightOn) => {
        if (cameraIndex === FRONT_CAMERA_INDEX) {
            setIsFrontCameraLightOn(isLightOn);
        }
        else if (cameraIndex === REAR_CAMERA_INDEX) {
            setIsRearCameraLightOn(isLightOn);
        }
    };
    const onSendSoftwareVersion = (version) => {
        var _a;
        console.log("Version: ", version);
        let caps = (_a = versions
            .find((v) => v.name === version)) === null || _a === void 0 ? void 0 : _a.capabilities.map((c) => c.name);
        console.log("caps", caps);
        if (caps) {
            setIsStickSend(caps.includes("StickPositions"));
            setIsAutopilot(caps.includes("Autopilot"));
            /*if(caps.find((item) => item === "StickPositions"))
            {
              setIsStickSend(true);
            } else {
              setIsStickSend(false)
            }*/
            setCapabilities(caps);
        }
    };
    useEffect(() => {
        window.addEventListener("beforeunload", endMission);
        window.addEventListener("popstate", endMission);
        return () => {
            window.removeEventListener("beforeunload", endMission);
            window.removeEventListener("popstate", endMission);
            endMission().catch();
        };
    }, []);
    useEffect(() => {
        storedMissionId.current = missionId;
    }, [missionId]);
    const endSession = async () => {
        onExit === null || onExit === void 0 ? void 0 : onExit();
        await endMission();
        if (connection) {
            connection.stop().then(() => {
                return true;
            });
        }
        navigate("/dashboard");
    };
    const openStarlinkSettings = (e) => {
        e.preventDefault();
        window.open(e.currentTarget.href, "starlink-settings", "popup,width=1280,height=900");
    };
    const endMission = async () => {
        if (connection && connection.state == HubConnectionState.Connected) {
            connection.stop();
        }
        console.log("End mission", storedMissionId.current);
        if (storedMissionId.current) {
            try {
                await missionService.endMission(storedMissionId.current);
            }
            catch (_a) { }
        }
    };
    const videoQualityChange = (value) => {
        setIsMainVideo(!value);
        if (connection) {
            connection.invoke("ChangeStream", !value);
        }
    };
    const rollbackStateChange = (value) => {
        rollbackState.current = value;
        setIsRollbackEnabled(value);
        if (connection) {
            connection.invoke("UpdateRollbackState", value);
        }
    };
    const resetCameras = async () => {
        if (droneId) {
            await videoService.resetCameras(droneId);
        }
    };
    const changeGear = (gear) => {
        if (connection) {
            connection.invoke("Speed", gear * 25);
        }
    };
    const resetGPS = () => {
        if (connection) {
            connection.invoke("ResetGPS");
        }
    };
    const handleCameraMode = () => {
        if (connection) {
            connection.invoke("SwitchCameraMode", !isCameraModeNight);
        }
    };
    const handleCameraLight = () => {
        if (connection) {
            connection.invoke("SwitchCameraLight", !isCameraLightOn);
        }
    };
    const isSeparateLight = capabilities.includes("SeparateLightSwitch");
    const handleFrontCameraLight = () => {
        if (connection) {
            if (cameras[FRONT_CAMERA_INDEX]) {
                connection.invoke("SwitchCameraLightV2", FRONT_CAMERA_INDEX, !isFrontCameraLightOn);
            }
        }
    };
    const handleRearCameraLight = () => {
        if (connection) {
            if (cameras[REAR_CAMERA_INDEX]) {
                connection.invoke("SwitchCameraLightV2", REAR_CAMERA_INDEX, !isRearCameraLightOn);
            }
        }
    };
    const handleAudio = () => {
        setIsAudio((prev) => !prev);
    };
    useEffect(() => {
        setMuteAudio(isAudio);
    }, [isAudio]);
    const handleMic = () => {
        setMic((prev) => !prev);
    };
    useEffect(() => {
        setMuteMic(isMic);
    }, [isMic]);
    /*useEffect(() => {
      setMicVolume(0.3);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);*/
    const handleVolume = (e) => {
        const val = Number(e.target.value);
        setSliderVolume(val);
        setMicVolume(val);
        setMic(false);
    };
    const showAutopilotError = (err) => {
        dispatch(setNotificationMessage({
            message: err.message,
            type: NotificationType.Error,
        }));
        dispatch(setNotificationStatus(true));
        throw err;
    };
    const startAutopilot = (seconds) => {
        if (!connection) {
            return Promise.reject();
        }
        return connection
            .invoke("StartAutopilot", seconds)
            .catch(showAutopilotError);
    };
    const stopAutopilot = () => {
        if (!connection) {
            return Promise.reject();
        }
        return connection.invoke("StopAutopilot").catch(showAutopilotError);
    };
    return (_jsxs("div", { className: "controller-bottom-wrapper", children: [!isViewOnly && (_jsxs(_Fragment, { children: [_jsx("div", { className: "controller-speed" }), _jsx(ArmedStatus, { isArmed: isArmed, setIsArmed: onArmedChange }), _jsx("div", { className: "two-col-wrapper", children: _jsx(VideoStatus, { isMainVideo: isMainVideo, videoQualityChange: videoQualityChange }) }), _jsx(DirectionControllerSpeed, { setGear: changeGear, gear: gear, isFull: isFullSpeed })] })), _jsxs("div", { className: "controller-bottom-actions", children: [!isViewOnly && (_jsxs(_Fragment, { children: [_jsx("button", { className: `btn reload ${isCruiseControlShown ? "cruise-control-active" : ""}`, title: t("droneControlBottomPanel.buttons.cruiseControl"), onClick: onCruiseControlClick, children: _jsx("img", { src: CONTROL_ICONS.cruiseControl, height: "25", width: "25", alt: "cruise-control-on" }) }), hasMic && (_jsx(_Fragment, { children: _jsx("button", { className: "btn reload", title: activeCameraHasSpeaker
                                        ? "Аудіо"
                                        : "Дана камера не має аудіо", onClick: handleAudio, children: isAudio ? (_jsx("img", { src: CONTROL_ICONS.audioOn, height: "25", width: "25", alt: "mute--v1" })) : (_jsx("img", { src: CONTROL_ICONS.audioOff, height: "25", width: "25", alt: "medium-volume--v1" })) }) })), hasSpeaker && (_jsxs("div", { className: "mic-volume-group", children: [_jsx("input", { type: "range", min: 0, max: 1, step: 0.01, value: sliderVolume, className: "mic-volume-slider", style: {
                                            background: `linear-gradient(to right, #f7ce46 ${sliderVolume * 100}%, #000 ${sliderVolume * 100}%)`,
                                        }, onChange: (e) => handleVolume(e), title: "Гучність динаміка на камері" }), _jsx("button", { className: "btn reload", title: activeCameraHasSpeaker
                                            ? "Мікрофон"
                                            : "Дана камера не має мікрофон", onClick: handleMic, children: isMic ? (_jsx("img", { src: droneSoundMute, height: "38", width: "38" })) : (_jsx("img", { src: droneSound, height: "38", width: "38" })) })] })), capabilities.includes("CameraLightSwitch") && (_jsx("div", { className: "camera-light-wrapper", children: isSeparateLight ? (_jsxs("div", { className: "camera-light-menu", children: [_jsxs("button", { className: isFrontCameraLightOn
                                                ? "btn reload active"
                                                : "btn reload", disabled: !isCameraModeNight, title: isCameraModeNight
                                                ? t("droneControlBottomPanel.buttons.frontCamera")
                                                : t("droneControlBottomPanel.buttons.cameraLightNightOnly"), onClick: handleFrontCameraLight, children: [_jsx("img", { style: { transform: "scaleX(-1)" }, src: CONTROL_ICONS.cameraLight, height: "25", width: "25", alt: "external-light-cars-components-those-icons-fill-those-icons-1" }), _jsx("span", { children: "\u041F" })] }), _jsxs("button", { className: isRearCameraLightOn ? "btn reload active" : "btn reload", disabled: !isCameraModeNight, title: isCameraModeNight
                                                ? t("droneControlBottomPanel.buttons.rearCamera")
                                                : t("droneControlBottomPanel.buttons.cameraLightNightOnly"), onClick: handleRearCameraLight, children: [_jsx("span", { children: "\u0417" }), _jsx("img", { src: CONTROL_ICONS.cameraLight, height: "25", width: "25", alt: "external-light-cars-components-those-icons-fill-those-icons-1" })] })] })) : (_jsx("button", { className: "btn reload", disabled: !isCameraModeNight, title: isCameraModeNight
                                        ? "Підсвітка камери"
                                        : "Підсвітка камери активна тільки в нічному режимі", onClick: handleCameraLight, children: isCameraLightOn ? (_jsx("img", { src: CONTROL_ICONS.cameraLightOn, height: "25", width: "25", alt: "Підсвітка камери" })) : (_jsx("img", { src: CONTROL_ICONS.cameraLightOff, height: "25", width: "25", alt: "Підсвітка камери" })) })) })), capabilities.includes("CameraModeSwitch") && (_jsx("button", { className: "btn reload", title: isCameraModeNight
                                    ? t("droneControlBottomPanel.buttons.nightMode")
                                    : t("droneControlBottomPanel.buttons.dayMode"), onClick: handleCameraMode, children: isCameraModeNight ? (_jsx("img", { src: CONTROL_ICONS.nightMode, height: "25", width: "25", alt: "night-camera" })) : (_jsx("img", { src: CONTROL_ICONS.dayMode, height: "25", width: "25", alt: "day-camera" })) })), hasParkingGridFoV && (_jsx("button", { className: showParkingGrid
                                    ? "btn reload parking-grid-btn active"
                                    : "btn reload parking-grid-btn", title: t("droneControlBottomPanel.buttons.parkingGrid"), onClick: () => dispatch(setShowParkingGrid(!showParkingGrid)), children: _jsx("img", { src: CONTROL_ICONS.parkingGrid, height: "25", width: "25", alt: t("droneControlBottomPanel.buttons.parkingGrid") }) })), _jsx("button", { className: "btn reload", title: t("droneControlBottomPanel.buttons.reloadCameras"), onClick: resetCameras, children: _jsx("img", { src: CONTROL_ICONS.reloadCameras, height: "25", width: "25", alt: "external-Refresh-video-actions-and-files-those-icons-fill-those-icons" }) }), showMap && (_jsx(Rnd, { default: {
                                    x: -50,
                                    y: -350,
                                    width: 320,
                                    height: 320,
                                }, enableResizing: {
                                    bottom: true,
                                    bottomRight: true,
                                    right: true,
                                    top: true,
                                    topLeft: true,
                                    topRight: true,
                                    left: true,
                                }, dragHandleClassName: "drag-handle", resizeHandleStyles: {
                                    bottomRight: {
                                        width: "20px",
                                        height: "20px",
                                        background: "rgba(0,0,0,0.1)",
                                        cursor: "nwse-resize",
                                    },
                                }, style: {
                                    zIndex: 10000,
                                }, onResizeStop: (e, direction, ref) => {
                                    setMapSize({ w: ref.offsetWidth, h: ref.offsetHeight });
                                }, children: _jsxs("div", { className: "drone-map", style: {
                                        width: "100%",
                                        height: "100%",
                                        position: "relative",
                                    }, children: [_jsx("div", { className: "drag-handle", style: {
                                                height: "5px",
                                                background: "#f7ce46",
                                                cursor: "move",
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                position: "absolute",
                                                width: "100%",
                                                top: "-1px",
                                                left: "0",
                                                zIndex: "1000",
                                            }, children: _jsx("div", { onClick: () => setShowMap(false), className: "close-drone" }) }), _jsx(Map, { lat: currPos.lat, lng: currPos.lng, azimuth: currAzimuth, routePoints: routePoints, destinationPoints: destinationPoints, missionId: missionId, isOnline: true, setSelectedLayer: setSelectedLayer, selectedLayer: selectedLayer }, `${mapSize.w}-${mapSize.h}`)] }) })), _jsx("button", { className: "btn reload", title: t("droneControlBottomPanel.buttons.openMap"), onClick: () => setShowMap((prev) => !prev), children: _jsx("img", { src: CONTROL_ICONS.map, height: "25", width: "25", alt: "map-marker" }) }), _jsx("button", { className: "btn reload", title: t("droneControlBottomPanel.buttons.reloadGps"), onClick: resetGPS, children: _jsx("img", { src: CONTROL_ICONS.reloadGps, height: "25", width: "25", alt: t("droneControlBottomPanel.buttons.reloadGps") }) }), isAutopilot && (_jsx("button", { className: "btn reload", title: t("droneControlBottomPanel.buttons.autopilot"), onClick: () => setShowAutopilot(true), children: _jsx("img", { src: CONTROL_ICONS.autopilot, width: "25", height: "25", alt: "driver" }) })), showAutopilot && (_jsx(Autopilot, { onStart: startAutopilot, onStop: stopAutopilot, onClose: () => setShowAutopilot(false) })), capabilities.includes("StarlinkStatus") && (_jsx("a", { className: "btn reload", href: "/starlink-settings/" + droneId + "?widget=1", target: "_blank", rel: "noopener noreferrer", onClick: openStarlinkSettings, title: t("droneControlBottomPanel.buttons.starlink"), children: _jsx("img", { src: CONTROL_ICONS.starlink, height: "25", width: "25", alt: "satellite-sending-signal" }) }))] })), _jsx("button", { onClick: endSession, className: "btn exit-camera-view", children: t("droneControlBottomPanel.buttons.exit") })] })] }));
};
